import React from 'react';
import { useNavigate } from 'react-router-dom';

const ValueCard = ({ icon, title, description }) => (
  <div style={{
    background: '#fff',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.06)',
    transition: 'box-shadow 0.3s ease, transform 0.3s ease',
  }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    <div style={{
      width: '48px', height: '48px', borderRadius: '10px',
      background: 'linear-gradient(135deg, #f3e8ff, #e0e7ff)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: '20px', fontSize: '22px',
    }}>{icon}</div>
    <h3 style={{
      fontSize: '18px', fontWeight: 600, color: '#1a1a2e',
      marginBottom: '10px',
    }}>{title}</h3>
    <p style={{
      fontSize: '15px', fontWeight: 400, lineHeight: 1.7,
      color: '#555', margin: 0,
    }}>{description}</p>
  </div>
);

const CapabilityRow = ({ num, title, description }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '50%',
      background: '#faf5eb', color: '#bb9749',
      fontSize: '15px', fontWeight: 700, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{num}</div>
    <div>
      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>{title}</h3>
      <p style={{ fontSize: '15px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: 0 }}>{description}</p>
    </div>
  </div>
);

const About = () => {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", marginBottom: '-80px' }}>

      {/* ── Hero ── */}
      <section style={{
        position: 'relative',
        padding: '100px 32px 80px',
        textAlign: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
        backgroundSize: '400% 400%',
        animation: 'aboutAurora 18s ease infinite',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(15, 10, 30, 0.35)', zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '760px', margin: '0 auto' }}>
          <p style={{
            fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
            textTransform: 'uppercase', color: '#bb9749', marginBottom: '16px',
          }}>About Divine Frameworks</p>
          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 700,
            lineHeight: 1.15, color: '#fff', margin: '0 0 24px',
          }}>
            Built for practitioners.<br />Trusted by seekers.
          </h1>
          <p style={{
            fontSize: 'clamp(16px, 2.5vw, 18px)', fontWeight: 400,
            lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', maxWidth: '680px',
            margin: '0 auto',
          }}>
            Divine Frameworks is the all-in-one platform where spiritual and wellness practitioners
            run their entire practice — and where seekers find trusted guides for their journey.
            Professional tools, real community, zero guesswork.
          </p>
        </div>
      </section>

      {/* ── What we built ── */}
      <section style={{ background: '#fff', padding: '80px 32px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <p style={{
            fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
            textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
          }}>What we built</p>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700,
            color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 20px',
          }}>
            Everything a practitioner needs, in one place.
          </h2>
          <p style={{
            fontSize: '16px', fontWeight: 400, lineHeight: 1.7,
            color: '#555', margin: '0 0 32px',
          }}>
            Divine Frameworks is an all-in-one platform where spiritual and wellness practitioners
            can build a professional website, manage bookings and payments, host events, communicate
            with clients, and grow their practice — all without needing to be a tech expert.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <CapabilityRow num="1" title="Professional websites" description="A beautiful, branded website in minutes — no coding, no design skills, no separate hosting. Just your practice, presented professionally." />
            <CapabilityRow num="2" title="Bookings & payments" description="Clients book and pay directly through your site. You keep 100% of session revenue. Calendar syncs automatically so you never double-book." />
            <CapabilityRow num="3" title="Events & workshops" description="Host workshops, group sessions, multi-week courses, or retreats — with built-in registration, payments, reminders, and Zoom integration." />
            <CapabilityRow num="4" title="Client management" description="Session notes, intake forms, messaging, and client history — everything organized in one place so you can focus on the work, not the admin." />
            <CapabilityRow num="5" title="AI-powered tools" description="Smart session prep, practice coaching, and insights — AI that makes practitioners better and more efficient, so they can focus on their talents." />
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section style={{ background: '#faf9ff', padding: '80px 32px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p style={{
              fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
              textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
            }}>Our values</p>
            <h2 style={{
              fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700,
              color: '#1a1a2e', margin: '0 0 16px',
            }}>
              What guides us
            </h2>
            <p style={{
              fontSize: '16px', fontWeight: 400, lineHeight: 1.7,
              color: '#555', maxWidth: '560px', margin: '0 auto',
            }}>
              These principles shape every feature we build and every decision we make.
            </p>
          </div>

          <div className="df-about-values" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }}>
            <ValueCard
              icon="🛡️"
              title="Trust & safety"
              description="Every practitioner is identity-verified and manually reviewed. Seekers book with confidence, not hope."
            />
            <ValueCard
              icon="💡"
              title="Simplicity"
              description="Powerful tools shouldn't be complicated. We handle the tech so practitioners can focus on their clients."
            />
            <ValueCard
              icon="🤝"
              title="Fair economics"
              description="No commissions on sessions. Practitioners keep 100% of their revenue — we earn through simple subscriptions."
            />
            <ValueCard
              icon="🌱"
              title="Growth"
              description="From first client to thriving practice — our tools, education, and AI features grow with every practitioner."
            />
            <ValueCard
              icon="🔮"
              title="Respect for the craft"
              description="We build specifically for the spiritual and wellness space — not as an afterthought to a generic booking platform."
            />
            <ValueCard
              icon="🌍"
              title="Accessibility"
              description="A free tier ensures anyone can start. Professional tools should be available to practitioners at every stage."
            />
          </div>
        </div>
      </section>

      {/* ── For seekers ── */}
      <section style={{ background: '#fff', padding: '80px 32px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{
            fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
            textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
          }}>For seekers</p>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700,
            color: '#1a1a2e', margin: '0 0 16px',
          }}>
            A better way to find practitioners you can trust.
          </h2>
          <p style={{
            fontSize: '16px', fontWeight: 400, lineHeight: 1.7,
            color: '#555', margin: '0 0 32px',
          }}>
            Browse verified practitioners across spiritual, healing, wellness, and personal growth
            practices. Read real reviews from real clients. Book sessions directly — no middlemen,
            no mystery. Every practitioner on Divine Frameworks has been reviewed by our team,
            so you can focus on your journey.
          </p>
          <button
            onClick={() => navigate('/directory')}
            style={{
              fontSize: '14px', fontWeight: 600, padding: '12px 28px',
              borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: '#bb9749', color: '#fff',
              fontFamily: "'Montserrat', sans-serif",
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#a6852f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#bb9749'; }}
          >Explore the directory</button>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: '#1a1a2e',
        padding: '80px 32px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700,
            color: '#fff', margin: '0 0 16px',
          }}>
            Your practice deserves better tools.
          </h2>
          <p style={{
            fontSize: '16px', fontWeight: 400, lineHeight: 1.7,
            color: 'rgba(255,255,255,0.65)', margin: '0 0 32px',
          }}>
            Join Divine Frameworks and get everything your practice needs so you can focus
            on what you do best - starting free.
          </p>
          <div className="df-about-cta-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/signup')}
              style={{
                fontSize: '14px', fontWeight: 600, padding: '12px 28px',
                borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: '#7c3aed', color: '#fff',
                fontFamily: "'Montserrat', sans-serif",
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#6d28d9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#7c3aed'; }}
            >Get started free</button>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                fontSize: '14px', fontWeight: 600, padding: '12px 28px',
                borderRadius: '6px', cursor: 'pointer',
                background: 'transparent', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                fontFamily: "'Montserrat', sans-serif",
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#1a1a2e';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#fff';
              }}
            >View pricing</button>
          </div>
        </div>
      </section>

      {/* ── Responsive + keyframes ── */}
      <style>{`
        @keyframes aboutAurora {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (max-width: 767px) {
          .df-about-values { grid-template-columns: 1fr !important; }
          .df-about-cta-buttons { flex-direction: column !important; align-items: center !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .df-about-values { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  );
};

export default About;
